import { BlobWithFilename, DocumentDownloadResult, documentApi } from 'apiConnector/document/document';
import { useStreamingContext } from 'contexts/streaming.context';
import { DocumentFormat } from 'epic/common-form/Documentation';
import type { DocumentType } from 'models/documents/DocumentType';
import { useCallback } from 'react';
import { first, lastValueFrom, mergeMap, of, throwError } from 'rxjs';

import { useGenerateBrochureDocument } from '../documents-legacy/DocumentButton.helper.brochure';

export async function generateDocument(
  locale: string,
  quoteQuery: unknown,
  documentType: DocumentType | string,
  productSubtype?: string,
  quoteId?: string,
  quoteState?: string,
  productType?: string,
): Promise<BlobWithFilename> {
  return await lastValueFrom(
    documentApi
      .generateDocumentV2<BlobWithFilename>(
        quoteQuery,
        documentType,
        locale,
        productSubtype,
        quoteId,
        quoteState,
        productType,
      )
      .pipe(),
  );
}

export function useGenerateDocument(
  quoteQuery: unknown,
  documentType: DocumentType | string,
  productSubtype?: string,
  quoteId?: string,
  quoteState?: string,
  productType?: string,
): (locale: string, documentFormat: DocumentFormat) => Promise<DocumentDownloadResult> {
  const { documentationMessage$ } = useStreamingContext();
  const generateBrochure = useGenerateBrochureDocument(quoteId);

  const generateUsualDocument = useCallback(
    (locale: string, documentFormat: DocumentFormat) => {
      return lastValueFrom(
        getDoc().pipe(
          mergeMap(doc => {
            if (doc === 'Accepted') {
              return documentationMessage$.pipe(
                first(
                  ({ docType, language, quoteId }) =>
                    docType === documentType && language === locale && quoteId === quoteId,
                ),
                mergeMap(event => {
                  if (event.type === 'NextDocumentationError') {
                    return throwError(() => ({ type: 'TECHNICAL' }));
                  } else {
                    return getDoc();
                  }
                }),
              );
            } else {
              return of(doc);
            }
          }),
        ),
      ) as Promise<BlobWithFilename>;

      function getDoc() {
        return documentApi.generateDocumentV2(
          quoteQuery,
          documentType,
          locale,
          productSubtype,
          quoteId,
          quoteState,
          productType,
          documentFormat,
        );
      }
    },
    [quoteQuery, documentType, productSubtype, quoteId, quoteState, productType, documentationMessage$],
  );

  return documentType === 'Brochure' ? generateBrochure : generateUsualDocument;
}
